import React from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { ErrorLike } from '@apollo/client';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  Task,
  TaskCompletion,
} from 'graphql-common';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import Table from '@lib/components/Table/Table';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import IconSurface from '@lib/components/IconSurface/IconSurface';
import { Colors } from '@lib/enums/colors';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import { ContextProps } from 'hooks/useOutletContext';
import TaskCompletionsModal from 'components/TaskCompletionsModal/TaskCompletionsModal';
import getTaskCompletionsTableActionsMenu from '@lib/components/TaskCompletions/utils/getTaskCompletionsTableActionsMenu';
import TasksReportingFilter from '../TasksReportingFilter/TasksReportingFilter';
import getTaskReportingTableActions from '../utils/getTaskReportingTableActions';
import useTaskReportingActions from '../utils/useTaskReportingActions';
import styles from '../TaskReportingView.module.scss';

type Props = {
  task?: Task;
  taskError?: ErrorLike;
  taskId?: string;
  taskLoading?: boolean;
};

export default function TaskReportingView(props: Props) {
  const { task, taskError, taskId, taskLoading } = props;
  const { t } = useTranslation();

  const { getUserAccessByCompany }: ContextProps = useOutletContext();

  const {
    filterCounter,
    filterDefaultValues,
    filterFields,
    minDate,
    maxDate,
    periodValue,
    onPeriodChange,
    onResetFilters,
    onSubmitFilter,
    tableColumns,
    tableData,
    tableLoading,
    tableFirstLoading,
    tableError,
    onViewTaskCompletionHandler,
    onCloseCompletionsModal,
    onCreateIntervention,
    onViewIntervention,
    selectedCompletionId,
    fetchIds,
    useBulkSelectResult,
    tableActionsForSelected,
    bulkCompareIds,
  } = useTaskReportingActions({ task, taskId });

  const isInterventionsLocked =
    getUserAccessByCompany(AccessComponentEnum.InterventionsManagement) ===
    AccessLevelEnum.LimitedRead;
  const actions = isInterventionsLocked
    ? getTaskReportingTableActions({ onViewTaskCompletionHandler })
    : undefined;
  const actionsMenu = isInterventionsLocked
    ? undefined
    : getTaskCompletionsTableActionsMenu({
        t,
        onViewCompletion: onViewTaskCompletionHandler,
        onCreateIntervention,
        onViewIntervention,
      });

  if (!taskId) {
    return <ErrorPage status={404} />;
  }

  if (taskError)
    return (
      <ErrorPage
        status={getStatusFromError(taskError)}
        description={getDescriptionFromError(taskError)}
      />
    );

  const filteredResultHint = (
    <Tooltip body={t('you-currently-see-filtered-result')}>
      <IconSurface
        icon="filter_alt"
        symbolsOutlined
        color={Colors.Blue}
        size="sm"
      />
    </Tooltip>
  );

  const emptyState = filterCounter ? (
    <ListEmptyState
      title={t('no-results-found')}
      text={t('try-something-else')}
    />
  ) : (
    <ListEmptyState
      title={t('no-task-reporting-yet')}
      text={t('no-task-reporting-text')}
    />
  );

  return (
    <>
      {taskLoading ? (
        <CircleLoader />
      ) : (
        <div className={styles.root}>
          <WhiteBox className={styles.headerCard}>
            <DashboardTitleHeader title={task?.name} bottomSpace="none" />
          </WhiteBox>
          <TasksReportingFilter
            counter={filterCounter}
            defaultValues={filterDefaultValues}
            filterFields={filterFields}
            minDate={minDate}
            maxDate={maxDate}
            onPeriodChange={onPeriodChange}
            onResetFilters={onResetFilters}
            onSubmitFilter={onSubmitFilter}
            periodValue={periodValue}
          />
          <WhiteBox className={styles.tableCard} horizontalPaddings="custom">
            {tableError ? (
              <ErrorPage
                status={getStatusFromError(tableError)}
                description={getDescriptionFromError(tableError)}
              />
            ) : (
              <div className={styles.tableWrapper}>
                {tableFirstLoading ? (
                  <CircleLoader />
                ) : (
                  <Table<TaskCompletion>
                    actions={actions}
                    actionsForSelected={tableActionsForSelected}
                    actionsMenu={actionsMenu}
                    columns={tableColumns}
                    data={tableData}
                    emptyState={emptyState}
                    hasSelectColumn
                    id="task-reporting-completions"
                    loading={tableLoading}
                    onRowClick={onViewTaskCompletionHandler}
                    totalEntries={tableData.length}
                    useBulkSelectResult={useBulkSelectResult}
                  />
                )}
              </div>
            )}
          </WhiteBox>
        </div>
      )}
      <TaskCompletionsModal
        task={selectedCompletionId || bulkCompareIds ? task || null : null}
        bucketId={null}
        initialCompletionId={selectedCompletionId}
        initialBulkCompareIds={bulkCompareIds}
        // Viewing one completion is opened straight from this screen's own
        // (filtered) table — there is no list behind it to go back to. Bulk
        // compare keeps the back arrow, as it always had.
        detailOnly={!bulkCompareIds}
        caption={bulkCompareIds ? undefined : t('completion-details')}
        renderAfterCaption={
          !bulkCompareIds && filterCounter ? filteredResultHint : undefined
        }
        // Prev/next and compare mode browse this screen's filtered set, not
        // the whole group.
        fetchIds={fetchIds}
        onClose={onCloseCompletionsModal}
      />
    </>
  );
}
